import React, { useCallback, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import Request from "../../core/requests";
import _ from "lodash";
import DataCurrentColorCard from "components/cards/DataCurrentColorCard";
import {
  Accordion,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import ErrorCardcomponent from "components/common/card/ErrorCard.component";
import { Loading } from "components/common";
import { optionsMetrics } from "constants/metrics";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { saveMetric } from "store/user.store";
import { useNavigate } from "react-router-dom";
import { refreshToken } from "store/user.store";
import { logout } from "store/user.store";
import { useTranslation } from "react-i18next";
import { IconInfoCircle } from "@tabler/icons";
import { DateTime } from "luxon";
import { getTimeZoneStandard } from "utils/timezone";

// constant
const headerSX = {
  "& .MuiCardHeader-action": { mr: 0 },
};

const CurrentDataContainer = ({
  zoneSelected,
  stationIdSelected,
  onFailure = () => { },
  refresh,
  isAccordion = true,
  isHome = false,
  sx,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, refresh_token, user } = useSelector((state) => state.user);
  const [dataCurrent, setDataCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewCards, setViewCards] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isFirstRefresh, setIsFirstRefresh] = useState(true);
  const [error, setError] = useState(false);

  const onClickCard = useCallback((item) => {
    if (item === "wind_speed" || item === "wind_dir_idx") {
      dispatch(
        saveMetric(
          optionsMetrics.filter((e) => e.value === "wind_speed,wind_dir_idx")[0]
        )
      );
    } else {
      if (item === "rain_intensity" || item === "rain_intensity_peak") {
        dispatch(
          saveMetric(
            optionsMetrics.filter(
              (e) => e.value === "rain_intensity,rain_intensity_peak"
            )[0]
          )
        );
      } else if (item === "pm10" || item === "pm2_5") {
        dispatch(
          saveMetric(optionsMetrics.filter((e) => e.value === "pm10,pm2_5")[0])
        );
      } else {
        dispatch(saveMetric(optionsMetrics.filter((e) => e.value === item)[0]));
      }
    }
    navigate("/dashboard/data-sensors/metrics");
    // eslint-disable-next-line
  }, []);

  const handleChange = useCallback(() => {
    if (isAccordion) setIsExpanded(!isExpanded);
  }, [isExpanded, isAccordion]);

  const onLogout = useCallback((error) => {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      dispatch(logout());
    }
    // eslint-disable-next-line
  }, []);

  const onError = useCallback(
    (error) => {
      setError(true);
      if (isFirstRefresh) {
        setLoading(false);
        if (error?.response?.status === 403) {
          onLogout(error);
        } else {
          if (error?.response?.status !== 401) {
            onFailure(error);
          } else {
            if (isFirstRefresh) {
              dispatch(
                refreshToken({
                  grant_type: "refresh_token",
                  refresh_token: user.refresh_token,
                })
              );
              setIsFirstRefresh(false);
            }
          }
        }
      } else {
        onLogout(error);
      }
    },
    // eslint-disable-next-line
    []
  );

  const onSuccess = useCallback((resp) => {
    setDataCurrent(resp);
    setLoading(false);
    setIsFirstRefresh(true);
    setError(false);
  }, []);

  useEffect(() => {
    setDataCurrent(null);
    setLoading(true);

    if (stationIdSelected) {
      Request.stations
        .getDataCurrentStationId({
          station_id: stationIdSelected,
          token: token,
        })
        .then((resp) => {
          onSuccess(resp);
        })
        .catch((error) => onError(error));
    } else {
      const path =
        zoneSelected && !_.isEmpty(zoneSelected)
          ? `?cluster=${zoneSelected}`
          : ``;
      Request.stations
        .getDataCurrentGeneric({ path: path, token: token })
        .then((resp) => {
          onSuccess(resp);
        })
        .catch((error) => onError(error));
    }
    // eslint-disable-next-line
  }, [zoneSelected, stationIdSelected, refresh, token, refresh_token]);

  useEffect(() => {
    if (dataCurrent)
      setViewCards(
        !_.isEmpty(
          Object.keys(dataCurrent).filter(
            (key) => dataCurrent[key]?.data !== null
          )
        )
      );
  }, [dataCurrent]);

  return loading ? (
    <Loading />
  ) : viewCards && !error ? (
    <Card sx={{ cursor: isAccordion ? "pointer" : "auto", ...sx }}>
      <Accordion expanded={isExpanded} onChange={handleChange}>
        <AccordionSummary
          expandIcon={isAccordion && <ExpandMoreIcon />}
          sx={{
            paddingLeft: 0,
            paddintTop: 0,
            paddingBottom: 0,
            maxHeight: 44,
            "&.MuiAccordionSummary-root.Mui-expanded": {
              minHeight: 47,
            },
            "&.MuiAccordionSummary-root:hover": {
              cursor: isAccordion ? "pointer" : "default",
            },
          }}
        >
          <CardHeader
            sx={headerSX}
            title={
              <Box>
                {t("Real-time data")}
                <Tooltip
                  title={
                    dataCurrent?.time ?
                      `${t("Updated to")}: ${DateTime.fromISO(
                        getTimeZoneStandard(dataCurrent?.time)
                      ).toLocaleString(DateTime.DATETIME_MED, {
                        locale: i18n?.language ? i18n.language : "it",
                      })}` :
                      `${t("Updated to")}: ${t('unknown')}`
                  }
                >
                  <IconButton sx={{ p: 0, pl: 0.5 }}>
                    <IconInfoCircle style={{ width: 13, height: 13 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          />
        </AccordionSummary>

        <Divider />
        <CardContent sx={{ "&.MuiCardContent-root": { padding: 2 } }}>
          <DataCurrentColorCard
            loading={loading}
            dataCurrent={dataCurrent}
            onClickCard={onClickCard}
            isHome={isHome}
          />
        </CardContent>
      </Accordion>
    </Card>
  ) : (
    <Box sx={{ width: "100%" }}>
      <ErrorCardcomponent message={t("no-data-present")} />
    </Box>
  );
};

export default CurrentDataContainer;
